import React, { useEffect } from 'react'
import styled from 'styled-components'

import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import { Extension } from '@tiptap/core'
import { Placeholder } from '@tiptap/extensions'

import './TextForm.scss'

import TextFormEditorButton from './TextFormEditorButton'
import RowSpacer from '../../styles/RowSpacer'

// Custom extension: typing '- ' converts the current line to a bullet list
// and typing a leading space converts it to a blockquote.
const MarkdownShortcuts = Extension.create({
    name: 'markdownShortcuts',
    addKeyboardShortcuts() {
        return {
            'Mod-e': () => this.editor.commands.toggleHighlight(),
            Space: () => {
                const { state, commands } = this.editor
                const { $from } = state.selection
                const lineText = $from.parent.textContent

                if (lineText === '-') {
                    commands.clearNodes()
                    commands.deleteRange({ from: $from.pos - 1, to: $from.pos })
                    return this.editor.commands.toggleBulletList()
                }

                if (lineText === '') {
                    return this.editor.commands.setBlockquote()
                }

                return false
            },
        }
    },
})

interface Props {
    placeholder: string
    initialContent?: string
    setContent: (content: string) => void
    height?: number
    autoFocus?: boolean
}

const TextForm: React.FC<Props> = ({
    placeholder,
    initialContent,
    setContent,
    height = 300,
    autoFocus = true,
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Highlight.configure({
                multicolor: false,
                HTMLAttributes: {
                    class: 'tiptap-highlight',
                },
            }),
            Placeholder.configure({
                placeholder,
            }),
            MarkdownShortcuts,
        ],
        content: initialContent || '',
        autofocus: autoFocus ? 'end' : false,
        onUpdate({ editor }) {
            setContent(editor.getHTML())
        },
    })

    // Sync initialContent when it changes (e.g. when editing an existing memo)
    useEffect(() => {
        if (!editor) return
        const current = editor.getHTML()
        if (current !== initialContent && initialContent !== undefined) {
            editor.commands.setContent(initialContent || '')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialContent])


    const labels = {
        quote: '인용',
        list: '리스트',
        bold: '굵게',
        italic: '기울게',
        underline: '밑줄',
        highlight: '강조',
    }

    return (
        <EditorFormContainer height={height}>
            <BlockButtonContainer>
                <TextFormEditorButton
                    label={labels.quote}
                    style="blockquote"
                    active={editor?.isActive('blockquote') ?? false}
                    onToggleStyle={() => editor?.chain().focus().toggleBlockquote().run()}
                />
                <TextFormEditorButton
                    label={labels.list}
                    style="bulletList"
                    active={editor?.isActive('bulletList') ?? false}
                    onToggleStyle={() => editor?.chain().focus().toggleBulletList().run()}
                />
            </BlockButtonContainer>
            <RowSpacer size={5} />
            <TextButtonContainer>
                <TextFormEditorButton
                    label={labels.bold}
                    style="BOLD"
                    active={editor?.isActive('bold') ?? false}
                    onToggleStyle={() => editor?.chain().focus().toggleBold().run()}
                />
                <TextFormEditorButton
                    label={labels.italic}
                    style="ITALIC"
                    active={editor?.isActive('italic') ?? false}
                    onToggleStyle={() => editor?.chain().focus().toggleItalic().run()}
                />
                <TextFormEditorButton
                    label={labels.underline}
                    style="UNDERLINE"
                    active={editor?.isActive('underline') ?? false}
                    onToggleStyle={() => editor?.chain().focus().toggleUnderline().run()}
                />
                <TextFormEditorButton
                    label={labels.highlight}
                    style="HIGHLIGHT"
                    active={editor?.isActive('highlight') ?? false}
                    onToggleStyle={() => editor?.chain().focus().toggleHighlight().run()}
                />
            </TextButtonContainer>
            <hr className="m-3" />

            <EditorContainer height={height} onClick={() => editor?.commands.focus()}>
                <EditorContent editor={editor} />
            </EditorContainer>
        </EditorFormContainer>
    )
}

interface ContainerProps {
    height: number
}

const EditorContainer = styled.div<ContainerProps>`
    padding: 0 16px 16px;
    overflow: scroll;
    height: ${(props) => props.height}px;
    background-color: transparent;

    .tiptap {
        outline: none;
        min-height: 100%;
        font-size: 16px;
        line-height: 1.5;
    }
`

const EditorFormContainer = styled.div<ContainerProps>`
    min-height: ${(props) => props.height}px;
    background-color: var(--search-field-bg-color);
    overflow-y: scroll;
    border: none;
    border-radius: 16px;
    padding: 12px;
    transition: background-color 0.2s ease;

    &:focus-within {
        background-color: var(--input-focus-bg-color);
    }
`

const TextButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    height: 100%;
`

const BlockButtonContainer = styled.div`
    display: flex;
    justify-content: center;
`

export default TextForm
